/**
 * Client-side capture detection for the ID-card steps. UX guards only — like
 * `livenessQuality`, these never replace the server-side checks; they only tell
 * the applicant whether a shot looks usable before they upload it.
 *
 * Three signals, matched to the two card sides:
 *  - frameAlign — the card actually fills the (already guide-cropped) frame with
 *    adequate exposure + detail. Reuses the pure `assessPixels` grader.
 *  - face — FRONT only: a portrait face is present, via the browser-native
 *    Shape Detection `FaceDetector`. Device-dependent → 'skipped' when the API is
 *    unavailable (never a false 'fail'). We deliberately do NOT pull in the heavy
 *    liveness FaceMesh model here — that is a multi-MB load unsuited to this step.
 *  - laser — BACK only: the laser code cannot be OCR'd in the browser (no OCR
 *    engine client-side), so it is 'deferred' — read + validated server-side after
 *    upload (see `thai_laser_ocr` / `extract_laser_code`).
 */
import type { DocumentSide } from './capture';
import { assessPixels, type QualityResult } from './livenessQuality';

/** Per-signal detection outcome. 'skipped'/'deferred' are non-blocking by design. */
export type DetectSignal = 'ok' | 'fail' | 'skipped' | 'deferred';

export interface CaptureDetection {
  /** Card fills the frame with usable exposure + detail. */
  frameAlign: DetectSignal;
  /** Front: portrait face present. Back / unsupported device: skipped. */
  face: DetectSignal;
  /** Back: laser code — verified server-side on submit. Front: skipped. */
  laser: DetectSignal;
  /** Raw quality grade behind `frameAlign` (exposes tooDark/tooBright/lowDetail). */
  quality: QualityResult;
}

/**
 * Grade the whole captured canvas (it is already cropped to the card guide, so
 * the full frame ≈ the card). Fail-open when 2d/getImageData is unavailable
 * (jsdom, tainted canvas) — a UX guard must never block on its own inability to run.
 */
export function gradeCardFrame(canvas: HTMLCanvasElement): QualityResult {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx || canvas.width < 2 || canvas.height < 2) {
    return { ok: true, issues: [] };
  }
  try {
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return assessPixels(data.data, 6);
  } catch {
    return { ok: true, issues: [] };
  }
}

/**
 * Count faces via the native Shape Detection API. Returns `undefined` when the
 * API is absent or throws — the caller maps that to a 'skipped' signal so an
 * unsupported device never produces a false negative.
 */
export async function countFaces(canvas: HTMLCanvasElement): Promise<number | undefined> {
  const Ctor = (globalThis as unknown as { FaceDetector?: unknown }).FaceDetector;
  if (typeof Ctor !== 'function') return undefined;
  try {
    const detector = new (Ctor as new (opts: unknown) => {
      detect(src: CanvasImageSource): Promise<unknown[]>;
    })({ fastMode: true, maxDetectedFaces: 1 });
    const faces = await detector.detect(canvas);
    return Array.isArray(faces) ? faces.length : 0;
  } catch {
    return undefined;
  }
}

/**
 * Run the per-side detection on a captured document canvas. Advisory only: the
 * signals guide the retake/submit decision, they do not gate it here.
 */
export async function detectCapture(
  canvas: HTMLCanvasElement,
  side: DocumentSide,
): Promise<CaptureDetection> {
  const quality = gradeCardFrame(canvas);
  const frameAlign: DetectSignal = quality.ok ? 'ok' : 'fail';

  let face: DetectSignal = 'skipped';
  let laser: DetectSignal = 'skipped';

  if (side === 'front') {
    const faces = await countFaces(canvas);
    face = faces === undefined ? 'skipped' : faces > 0 ? 'ok' : 'fail';
  } else {
    // Laser code is OCR'd + validated server-side after upload.
    laser = 'deferred';
  }

  return { frameAlign, face, laser, quality };
}
